import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import "../../../style/tasks.css";

const ExpandedTaskView = ({ fromDate, toDate, tasks ,recholidayCalendarArray}) => {
  // --- build week ranges ---
    let validAbsentDate = null;
    // --- collect all valid absent dates ---
const absentDateArray = useMemo(() => {
  return tasks
    .filter(
      (task) =>
        task.absentDate &&
        task.absentDate !== "" &&
        task.absentDate !== "01/01/2001 00:00:00" &&
        task.absentDate !== "01/01/0001 00:00:00"
    )
    .map((task) =>
      moment(task.absentDate, ["MM/DD/YYYY HH:mm:ss", "YYYY-MM-DD"]).format("YYYY-MM-DD")
    );
}, [tasks]);

  const getWeeksBetweenDates = (fromDate, toDate) => {
    const start = moment(fromDate).startOf("isoWeek");
    const end = moment(toDate).endOf("isoWeek");

    const weeks = [];
    let current = start.clone();

    while (current.isSameOrBefore(end)) {
      const weekEnd = current.clone().endOf("isoWeek");
      weeks.push({
        start: current.clone(),
        end: weekEnd,
        label: `${current.format("MMM D")} - ${weekEnd.format("MMM D")}`,
      });
      current.add(1, "week");
    }

    return weeks;
  };

  const weeks = useMemo(
    () => getWeeksBetweenDates(fromDate, toDate),
    [fromDate, toDate]
  );

  const currentWeekIndex = weeks.findIndex((week) =>
    moment().isBetween(week.start, week.end, null, "[]")
  );

  const [activeWeekIndex, setActiveWeekIndex] = useState(
    currentWeekIndex >= 0 ? currentWeekIndex : 0
  );

  // --- precompute cumulative totals ---
  const [fixedCumulativeMap, setFixedCumulativeMap] = useState({});
  useEffect(() => {
    const cumulativeMap = {};
    const cumulativeTasks = tasks.filter((task) =>
      moment(task.start).isBetween(
        weeks[0].start,
        weeks[weeks.length - 1].end,
        null,
        "[]"
      )
    );

    cumulativeTasks.forEach((task) => {
      const taskKey = `${task.projectName || "Unknown Project"}__${task.resourceId}`;
      const hours = parseFloat(task.hoursspent || 0);
      if (!cumulativeMap[taskKey]) cumulativeMap[taskKey] = 0;
      cumulativeMap[taskKey] += hours;
    });

    setFixedCumulativeMap(cumulativeMap);
  }, [tasks, weeks]);

  // --- navigation ---
  const addWeek = (direction) => {
    const nextIndex =
      direction === "next" ? activeWeekIndex + 1 : activeWeekIndex - 1;
    if (nextIndex >= 0 && nextIndex < weeks.length) {
      setActiveWeekIndex(nextIndex);
    }
  };

  const selectedWeek = weeks[activeWeekIndex];
  const getTaskKey = (task) =>
    `${task.projectName || "Unknown Project"}__${task.resourceId}`;

  // --- group only current week tasks ---
  const groupedByProject = {};
  tasks
    .filter((t) =>
      moment(t.start).isBetween(selectedWeek.start, selectedWeek.end, null, "[]")
    )
    .forEach((task) => {
      if (task.isLeave) {
        return;
      }

      const projectName = task.projectName || "Unknown Project";
      const key = getTaskKey(task);

      if (!groupedByProject[projectName]) groupedByProject[projectName] = {};
      if (!groupedByProject[projectName][key]) {
        groupedByProject[projectName][key] = {
          resourceId: task.resourceId,
          title: task.resourceId,
          daily: {},
          weekTotal: 0,
          cumulativeTotal: fixedCumulativeMap[key] || 0,
          isLeave :task.isLeave,
          status: task.status,
          absentDate: task.absentDate,
        };
      }

     const dateKey = moment(task.start).format("YYYY-MM-DD");
const hours = parseFloat(task.hoursspent || 0);

if (!groupedByProject[projectName][key].daily[dateKey]) {
  groupedByProject[projectName][key].daily[dateKey] = { hours: 0, status: task.status,absentDate:task.absentDate };
}

groupedByProject[projectName][key].daily[dateKey].hours += hours;
groupedByProject[projectName][key].daily[dateKey].absentDate= task.absentDate;
      groupedByProject[projectName][key].weekTotal += hours;
    });

  // --- precompute leave days set ---
  const leaveDaysSet = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => {
      if (t.isLeave) {
        const taskStart = moment(t.start, ["MM/DD/YYYY HH:mm:ss", "YYYY-MM-DD"]);
        const taskEnd = moment(t.end, ["MM/DD/YYYY HH:mm:ss", "YYYY-MM-DD"]);
        let current = taskStart.clone();
        while (current.isSameOrBefore(taskEnd, "day")) {
          set.add(current.format("YYYY-MM-DD"));
          current.add(1, "day");
        }
      }
    });
    return set;
  }, [tasks]);

  // --- build all days for current week ---
  const allDisplayedDates = Array.from({ length: 7 }, (_, i) => {
    const day = selectedWeek.start.clone().add(i, "days");
    return {
      label: day.format("ddd"),
      date: day.format("YYYY-MM-DD"),
      display: day.format("DD MMM"),
    };
  });

  // --- totals helpers ---
 const getDailyTotal = (date) =>
  Object.values(groupedByProject)
    .flatMap((project) =>
      Object.values(project).map((task) => task.daily[date]?.hours || 0)
    )
    .reduce((a, b) => a + b, 0);
  // --- cell color rules ---
const getCellStyle = (daily, date, task) => {
  const isHoliday = recholidayCalendarArray?.some(
    (holiday) => moment(holiday, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD") === date
  );
  const isWeekend = moment(date).day() === 0 || moment(date).day() === 6; // Sunday=0, Saturday=6
  const isLeave = leaveDaysSet.has(date);

  // Find a valid absentDate for this task

  //  if (absentDateArray.includes(date)) {
  //   return { backgroundColor: "orange" };
  // }
  
  if ((isHoliday || isWeekend) && daily.hours > 0) {
    return { backgroundColor: "lightyellow" }; // AC on holiday
  }
  if (isHoliday || isWeekend) {
    return { backgroundColor: "lightcoral" }; // holiday/weekend
  }
  if (isLeave) {
    return {}; // No color for leave days with zero hours
  }
  if (!daily || daily.hours === 0) return { backgroundColor: "lightblue" }; // Not Applied
  if (daily.status === "Approved") return { backgroundColor: "lightgreen" };
  return { backgroundColor: "yellow" }; // Pending
};


  return (
    <div className="container mt-4">
      {/* Header with Week & Arrows */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5>
            <strong>Week:</strong>{" "}
            <span style={{ color: "#007BFF", fontSize: "1rem" }}>
              {selectedWeek.start.format("MMM D")} - {selectedWeek.end.format("MMM D")}
            </span>
          </h5>
        </div>
        <div>
          <span
            className="arrow-button"
            onClick={() => addWeek("prev")}
            style={{ opacity: activeWeekIndex === 0 ? 0.3 : 1 }}
          >
            <i className="fas fa-arrow-left"></i>
          </span>
          <span
            className="arrow-button"
            onClick={() => addWeek("next")}
            style={{ opacity: activeWeekIndex === weeks.length - 1 ? 0.3 : 1 }}
          >
            <i className="fas fa-arrow-right"></i>
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "lightblue", marginRight: "5px" }}></div>
          <span>Not Applied</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "lightyellow", marginRight: "5px" }}></div>
          <span>AC</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "lightcoral", marginRight: "5px" }}></div>
          <span>Holiday</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "yellow", marginRight: "5px" }}></div>
          <span>WFH / AO / WFO (Pending)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "lightgreen", marginRight: "5px" }}></div>
          <span>WFH / AO / WFO (Approved)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", background: "lightpink", marginRight: "5px" }}></div>
          <span>Leave</span>
        </div>
      </div>

      {/* Table */}
      <div className="scroll-table-wrapper">
        <table className="table table-bordered table-sm text-center">
          <thead className="thead-light sticky-header">
            <tr>
              <th className="text-start" style={{ width: "95px" }}>Project Name</th>
              <th className="text-start">Task</th>
              {allDisplayedDates.map(({ label, display, date }) => (
                <th key={date} style={{ width: "60px" }}>
                  {label}<br />
                  <small style={{ fontSize: "11px" }}>{display}</small>
                </th>
              ))}
              <th style={{ width: "89px" }}>Week Total</th>
              <th style={{ width: "118px" }}>Cumulative Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedByProject).map(([projectName, tasks], i) => (
              <React.Fragment key={i}>
                <tr>
                  <td colSpan={2} className="fw-bold text-start bg-light profontstyle">
                    {projectName}
                  </td>
                </tr>
                {Object.values(tasks).map((task, idx) => (
                  <tr key={idx}>
                    <td></td>
                    <td className="text-start profontstyletask" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{task.title}</td>
                    {allDisplayedDates.map(({ date }) => {
                      const dailyValue = task.daily[date] || 0;
                      const hasLeave = leaveDaysSet.has(date);
                      return (
                        <td key={date} style={getCellStyle(dailyValue, date, task)}>
                          {dailyValue?.hours  || "-"}
                        </td>
                      );
                    })}
                    <td><strong>{parseFloat(task.weekTotal)}</strong></td>
                    <td><strong>{parseFloat(task.cumulativeTotal)}</strong></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="sticky-footer">
  <tr>
    <td></td>
    <td className="profontstyletask"><strong>Totals</strong></td>
    {allDisplayedDates.map(({ date }) => {
      const dailyTotal = getDailyTotal(date);
      const hasLeave = leaveDaysSet.has(date);

      // ✅ Apply color ONLY if leave
      const style = hasLeave
        ? { backgroundColor: "lightpink" }
        : {};

      return (
        <td key={date} style={style}>
          <strong>{dailyTotal  || "-"}</strong>
        </td>
      );
    })}
    <td>
      <strong>
        {Object.values(groupedByProject)
          .flatMap((project) => Object.values(project).map((task) => task.weekTotal))
          .reduce((sum, val) => sum + val, 0)}
      </strong>
    </td>
    <td>
      <strong>
        {Object.values(groupedByProject)
          .flatMap((project) => Object.values(project).map((task) => task.cumulativeTotal))
          .reduce((sum, val) => sum + val, 0)}
      </strong>
    </td>
  </tr>
</tfoot>

        </table>
      </div>
    </div>
  );
};

export default ExpandedTaskView;
